import { useEffect, useState } from "react";
import gsap from "gsap";
import SplitText from "gsap/SplitText";

type KnockoutColor = "white" | "black";

// Boxes each rendered line of a knockout text element via GSAP SplitText.
// `ready` stays false until the split exists so callers can hold the reveal
// back and never flash the untight baseline.
export function useKnockoutLines(
  ref: React.RefObject<HTMLElement | null>,
  { enabled, color, resplitKey }: { enabled: boolean; color: KnockoutColor; resplitKey?: unknown }
): { ready: boolean } {
  const [ready, setReady] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setReady(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    gsap.registerPlugin(SplitText);
    let split: SplitText | null = null;
    let cancelled = false;

    const run = () => {
      if (cancelled || !ref.current) return;
      split = SplitText.create(ref.current, {
        type: "lines",
        linesClass: `dx-knockout--line dx-knockout--${color}`,
        autoSplit: true,
        // SplitText's default aria mode stamps aria-label on the target, which
        // is prohibited on role-less <p> (axe: aria-prohibited-attr). The line
        // spans keep the real text, so screen readers read them directly.
        aria: "none",
        onSplit: (self: SplitText) => {
          self.lines.forEach((line) => (line as HTMLElement).setAttribute("aria-hidden", "true"));
        },
      });
      ref.current.classList.add("dx-knockout-split");
      setReady(true);
    };

    // Fonts must be loaded or line measurement is wrong.
    if (typeof document !== "undefined" && document.fonts?.status === "loaded") {
      run();
    } else {
      document.fonts.ready.then(run);
    }

    return () => {
      cancelled = true;
      split?.revert();
      ref.current?.classList.remove("dx-knockout-split");
      setReady(false);
    };
  }, [enabled, color, resplitKey, ref]);

  return { ready };
}
